/**
 * BulkChangeModal — „Zmień zbiorczo" (akcja jednorazowa, PHASE 25 Faza 2 §8).
 *
 * Odchudzony brat edytora akcji własnych (`ListActionsSettingsTab` → `ActionEditorModal`):
 * BEZ nazwy, ikony, ról, „Zapisz akcję" i BEZ SQL. Zbiera LISTĘ zmian (ustaw pole / zmień
 * kategorię / dodaj-usuń grupę) i woła wsadowy endpoint `/list-actions/bulk-change`, który
 * wykonuje wszystko w JEDNEJ transakcji na zaznaczonych rekordach (porażka → rollback całości).
 *
 * Dostępność operacji zależy od kartoteki (parytet z edytorem definicji):
 *  - „Ustaw pole"       — kartoteki systemowe (katalog pól z `/system-modules/:slug/fields`),
 *  - „Zmień kategorię"  — kartoteki z osią kategorii (bez dokumentów),
 *  - „Dodaj/Usuń grupę" — kartoteki z polem `groups` (kontrahenci/pracownicy/użytkownicy/firmy/
 *                         kontakty).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BulkChangeOperation,
  GROUPS_DICTIONARY_BY_KARTOTEKA,
  isRecordCategoryKartotekaKey,
  kartotekaHasGroups,
  type ListActionKartotekaKey,
} from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useToast } from '@/contexts/ToastContext';
import { useDictionary } from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import {
  draftValueToApi,
  isExcludedFromBulkSet,
  valueControlKind,
} from '@/lib/list-actions/value-control';

type OperationKind = BulkChangeOperation['kind'];

/** Roboczy stan JEDNEJ zmiany w modalu (przed złożeniem w `BulkChangeOperation`). */
interface OperationDraft {
  /** Klucz Reacta (stabilny przy dodawaniu/usuwaniu wierszy). */
  key: string;
  kind: OperationKind;
  /** set_field */
  field: string;
  value: string;
  /** set_category */
  categoryId: string | null;
  /** add_group / remove_group — wybrane kody grup (każdy → osobna operacja przy składaniu). */
  groupIds: string[];
}

interface FieldOptionRow {
  value: string;
  label: string;
}
interface EditorFieldRow {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  /** Statyczne opcje pola `select` (z Kreatora) — `{value,label}[]`. */
  options?: FieldOptionRow[];
  /** Słownik pola `select` (globalny/tenanta) — alternatywne źródło opcji. */
  dictionaryCode?: string | null;
  /** Pole tylko-do-odczytu (np. z Symfonii) — nie do „Ustaw pole". */
  readOnly?: boolean;
}

/** Czy pole `select` ma znane źródło dozwolonych wartości (statyczne opcje albo słownik). */
function selectHasSource(f: EditorFieldRow): boolean {
  return !!f.dictionaryCode || (Array.isArray(f.options) && f.options.length > 0);
}
/**
 * Czy pole nadaje się do „Ustaw pole" w trybie zbiorczym. Referencje i pola tylko-do-odczytu →
 * NIE. Multiselect (tablicowe, np. grupy) → NIE (grupy mają osobne operacje). Select bez znanego
 * źródła opcji → NIE (nie umiemy dać dropdownu ani zwalidować; jego wartości żyją w kodzie, nie
 * w katalogu — patrz backend).
 */
function fieldIsBulkSettable(f: EditorFieldRow): boolean {
  if (isExcludedFromBulkSet(f)) return false;
  const ft = f.fieldType.toLowerCase();
  if (ft === 'multiselect') return false;
  if (ft === 'select') return selectHasSource(f);
  return true;
}

/** Kontrolka wartości dla pola `select` — dropdown z opcji statycznych albo ze słownika. */
function SelectValueControl({
  field,
  value,
  onChange,
  testId,
}: {
  field: EditorFieldRow;
  value: string;
  onChange: (v: string) => void;
  testId?: string;
}) {
  const dict = useDictionary(field.dictionaryCode ?? '');
  const options: FieldOptionRow[] = field.dictionaryCode
    ? dict.activeItems.map((i) => ({ value: i.code, label: i.label }))
    : (field.options ?? []);
  return (
    <SearchableSelect
      options={options}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      allowClear
      data-testid={testId}
    />
  );
}

let draftSeq = 0;
function emptyDraft(kind: OperationKind): OperationDraft {
  draftSeq += 1;
  return { key: `op-${draftSeq}`, kind, field: '', value: '', categoryId: null, groupIds: [] };
}

export interface BulkChangeModalProps {
  open: boolean;
  onClose: () => void;
  /** Kartoteka listy (klucz osi / `generic`). */
  kartotekaKey: ListActionKartotekaKey;
  /** Slug kartoteki systemowej → fizyczna tabela encji (run-endpoint). */
  slug: string;
  /** Zaznaczone rekordy. */
  selectedIds: string[];
  /** Wywołane po udanym wykonaniu (invalidacja listy + wyczyszczenie zaznaczenia). */
  onDone: () => void;
}

export function BulkChangeModal({
  open,
  onClose,
  kartotekaKey,
  slug,
  selectedIds,
  onDone,
}: BulkChangeModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [drafts, setDrafts] = useState<OperationDraft[]>([emptyDraft('set_field')]);
  const [formError, setFormError] = useState<string | null>(null);

  const isDocuments = kartotekaKey === 'documents';
  const hasCategories = !isDocuments && isRecordCategoryKartotekaKey(kartotekaKey);
  const hasGroups = kartotekaHasGroups(kartotekaKey);
  const groupsDictionaryCode = hasGroups
    ? GROUPS_DICTIONARY_BY_KARTOTEKA[kartotekaKey as keyof typeof GROUPS_DICTIONARY_BY_KARTOTEKA]
    : '';

  const opKinds: { id: OperationKind; label: string }[] = useMemo(() => {
    const out: { id: OperationKind; label: string }[] = [
      { id: 'set_field', label: t('tenant.listActions.editor.operations.setField') },
    ];
    if (hasCategories) {
      out.push({
        id: 'set_category',
        label: t('tenant.listActions.editor.operations.setCategory'),
      });
    }
    if (hasGroups) {
      out.push({ id: 'add_group', label: t('tenant.listActions.editor.operations.addGroup') });
      out.push({
        id: 'remove_group',
        label: t('tenant.listActions.editor.operations.removeGroup'),
      });
    }
    return out;
  }, [hasCategories, hasGroups, t]);

  useEffect(() => {
    if (!open) return;
    setDrafts([emptyDraft('set_field')]);
    setFormError(null);
  }, [open]);

  // Katalog pól kartoteki (dla „Ustaw pole"). Pobierany raz, współdzielony przez wszystkie zmiany.
  const fieldsQuery = useQuery({
    queryKey: ['system-module-fields', slug],
    queryFn: async () => {
      const res = await api.get<{ fields: EditorFieldRow[] }>(`/system-modules/${slug}/fields`);
      return res.data.fields;
    },
    enabled: open && slug !== '',
  });
  const fields = fieldsQuery.data ?? [];
  // Tylko pola, które umiemy bezpiecznie ustawić zbiorczo (multiselect i select-bez-źródła poza).
  const settableFields = fields.filter(fieldIsBulkSettable);
  const fieldOptions = settableFields.map((f) => ({ value: f.slug, label: f.name }));

  // Kategorie kartoteki (dla „Zmień kategorię").
  const categoriesQuery = useQuery({
    queryKey: ['record-categories', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ categories: { id: string; name: string }[] }>(
        '/record-categories',
        { params: { kartoteka: kartotekaKey } },
      );
      return res.data.categories;
    },
    enabled: open && hasCategories,
  });
  const categoryOptions = (categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  function patchDraft(key: string, patch: Partial<OperationDraft>): void {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }

  function addDraft(): void {
    setDrafts((prev) => [...prev, emptyDraft('set_field')]);
  }

  function removeDraft(key: string): void {
    setDrafts((prev) => (prev.length <= 1 ? prev : prev.filter((d) => d.key !== key)));
  }

  /** Składa `BulkChangeOperation[]` z draftów; null gdy którakolwiek zmiana niekompletna. */
  function buildOperations(): BulkChangeOperation[] | null {
    const ops: BulkChangeOperation[] = [];
    for (const d of drafts) {
      if (d.kind === 'set_field') {
        if (!d.field || d.value === '') return null;
        const fieldType = settableFields.find((f) => f.slug === d.field)?.fieldType ?? '';
        // Data z godziną: czas lokalny z inputu → ISO ze strefą; niepoprawna wartość = brak zmiany.
        const value = draftValueToApi(valueControlKind(fieldType), d.value);
        if (value === null) return null;
        ops.push({ kind: 'set_field', field: d.field, value });
      } else if (d.kind === 'set_category') {
        if (!d.categoryId) return null;
        ops.push({ kind: 'set_category', categoryId: d.categoryId });
      } else {
        // add_group / remove_group — każdy wybrany kod grupy → osobna operacja.
        if (d.groupIds.length === 0) return null;
        for (const code of d.groupIds) ops.push({ kind: d.kind, groupId: code });
      }
    }
    return ops.length > 0 ? ops : null;
  }

  const runMutation = useMutation({
    mutationFn: async () => {
      const operations = buildOperations();
      if (!operations) throw new Error('INCOMPLETE');
      await api.post('/list-actions/bulk-change', {
        kartotekaKey,
        slug,
        selectedIds,
        operations,
      });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.listActions.toast.done', { count: selectedIds.length }),
      });
      onDone();
      onClose();
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'INCOMPLETE') {
        setFormError(t('tenant.listActions.bulkChange.incomplete'));
        return;
      }
      if (isAxiosError(err)) {
        const status = err.response?.status;
        const code = (err.response?.data as { error?: string } | undefined)?.error;
        // Konflikt unikalności (np. ten sam e-mail/login na kilku rekordach) — cała zmiana cofnięta.
        if (status === 409 || code === 'UNIQUE_VIOLATION') {
          setFormError(t('tenant.listActions.bulkChange.uniqueViolation'));
          return;
        }
        // Któraś zmiana odrzucona przez walidację (nieznane/read-only pole, niedozwolona wartość,
        // zła grupa/kategoria).
        if (status === 400) {
          setFormError(t('tenant.listActions.bulkChange.invalidChange'));
          return;
        }
      }
      toast.show({ variant: 'error', title: t('tenant.listActions.errorGeneric') });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="2xl"
      title={t('tenant.listActions.bulkChange.title', { count: selectedIds.length })}
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          {formError && (
            <span className="text-xs text-text-danger" data-testid="bulk-change-error">
              {formError}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={runMutation.isPending}
            onClick={() => {
              setFormError(null);
              runMutation.mutate();
            }}
            data-testid="bulk-change-run"
          >
            {t('tenant.listActions.bulkChange.run', { count: selectedIds.length })}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="bulk-change-modal">
        <p className="text-sm text-text-secondary">
          {t('tenant.listActions.bulkChange.intro', { count: selectedIds.length })}
        </p>

        {drafts.map((d, i) => {
          const fieldMeta = settableFields.find((f) => f.slug === d.field);
          const valueKind = valueControlKind(fieldMeta?.fieldType ?? '');
          return (
            <div
              key={d.key}
              className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-bg-secondary p-3"
              data-testid={`bulk-change-op-${i}`}
            >
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={opKinds.map((o) => ({ value: o.id, label: o.label }))}
                    value={d.kind}
                    onChange={(v) =>
                      patchDraft(d.key, {
                        kind: (v as OperationKind) ?? 'set_field',
                        field: '',
                        value: '',
                        categoryId: null,
                        groupIds: [],
                      })
                    }
                    data-testid={`bulk-change-op-${i}-kind`}
                  />
                </div>
                {drafts.length > 1 && (
                  <IconButton
                    aria-label={t('tenant.listActions.bulkChange.removeOp')}
                    variant="ghost"
                    onClick={() => removeDraft(d.key)}
                    data-testid={`bulk-change-op-${i}-remove`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </IconButton>
                )}
              </div>

              {d.kind === 'set_field' && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <SearchableSelect
                    options={fieldOptions}
                    value={d.field || null}
                    onChange={(v) => patchDraft(d.key, { field: v ?? '', value: '' })}
                    placeholder={t('tenant.listActions.editor.fieldPlaceholder')}
                    allowClear
                    data-testid={`bulk-change-op-${i}-field`}
                  />
                  {valueKind === 'select' && fieldMeta ? (
                    <SelectValueControl
                      field={fieldMeta}
                      value={d.value}
                      onChange={(v) => patchDraft(d.key, { value: v })}
                      testId={`bulk-change-op-${i}-value`}
                    />
                  ) : valueKind === 'bool' ? (
                    <Checkbox
                      label={t('tenant.listActions.editor.valueBool')}
                      checked={d.value === 'true'}
                      onChange={(e) =>
                        patchDraft(d.key, { value: e.target.checked ? 'true' : 'false' })
                      }
                      disabled={!d.field}
                    />
                  ) : valueKind === 'date' ? (
                    <DatePicker
                      value={d.value}
                      onChange={(v) => patchDraft(d.key, { value: v })}
                      disabled={!d.field}
                      aria-label={t('tenant.listActions.editor.value')}
                      data-testid={`bulk-change-op-${i}-value`}
                    />
                  ) : valueKind === 'datetime' ? (
                    <Input
                      type="datetime-local"
                      value={d.value}
                      onChange={(e) => patchDraft(d.key, { value: e.target.value })}
                      disabled={!d.field}
                      aria-label={t('tenant.listActions.editor.value')}
                      data-testid={`bulk-change-op-${i}-value`}
                    />
                  ) : (
                    <Input
                      type={valueKind === 'number' ? 'number' : 'text'}
                      value={d.value}
                      onChange={(e) => patchDraft(d.key, { value: e.target.value })}
                      disabled={!d.field}
                      placeholder={t('tenant.listActions.editor.value')}
                      data-testid={`bulk-change-op-${i}-value`}
                    />
                  )}
                </div>
              )}

              {d.kind === 'set_category' && (
                <SearchableSelect
                  options={categoryOptions}
                  value={d.categoryId}
                  onChange={(v) => patchDraft(d.key, { categoryId: v })}
                  placeholder={t('tenant.listActions.editor.categoryPlaceholder')}
                  allowClear
                  data-testid={`bulk-change-op-${i}-category`}
                />
              )}

              {(d.kind === 'add_group' || d.kind === 'remove_group') && (
                <GroupsInput
                  dictionaryCode={groupsDictionaryCode}
                  value={d.groupIds}
                  onChange={(codes) => patchDraft(d.key, { groupIds: codes })}
                  data-testid={`bulk-change-op-${i}-group`}
                />
              )}
            </div>
          );
        })}

        <div>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={addDraft}
            data-testid="bulk-change-add-op"
          >
            {t('tenant.listActions.bulkChange.addOp')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
